
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import TextButton from '@/components/shared/suites/TextButton.vue';

@Component<CalendarContent>({
	components: {
		TextButton
	}
})
export default class CalendarContent extends Vue {
	@Prop({ type: String, required: true }) calendarType!: 'month' | 'week' | 'day';
	@Prop({ type: Object, required: true }) selectedRange!: { start: string; end: string };
	@Prop({ type: Array, required: true }) calendarEvents!: CalendarEvent[] | MonthlyCalendarEvent[];
	@Prop({ type: Object, required: true }) restaurant!: Restaurant;
	@Prop({ type: Boolean, required: false, default: false }) loading!: boolean;
	@Action('setTakeoutInformation', { namespace: 'orders' }) private setTakeoutInformation!: (takeoutInformation: Pickup) => void;
	@Getter('getTakeoutInformation', { namespace: 'orders' }) private pickupInformation!: Pickup;
	@Getter('getTimezone', { namespace: 'auth' }) private timezone!: string;
	@Watch('isCreateOrderMenuOpen')
	deleteTempOrderOnMenuClose() {
		if (this.tempCalendarEvent && !this.isCreateOrderMenuOpen) {
			this.tempCalendarEvent = null;
		}
	}

	$refs!: {
		calendar: any;
	};

	private calendarMounted: boolean = false;
	private isCreateOrderMenuOpen: boolean = false;
	private tempCalendarEvent: TempCalendarEvent | null = null;
	private clickedElement: HTMLElement | null = null;
	private tempOrderTime: string | null = null;
	private locale: string = this.$i18n.locale;
	private timeUpdateInterval: number | undefined;
	private parentContainerWidth: number | undefined = 0;
	private menuPositionX: number = 0;
	private menuPositionY: number = 0;

	/**
	 * Get the current selected month number for filtering month view day headers
	 *
	 * @return {number}
	 */
	private get selectedMonth(): number {
		return DateTime.fromISO(this.selectedRange.start).month;
	}

	/**
	 * Calculate current time position in pixels for the current time indicator
	 *
	 * @return {number | null}
	 */
	private get currentTime(): number | null {
		const timezoneDiffInMinutes = DateTime.now().setZone(this.timezone).offset - DateTime.now().offset;
		return this.calendarMounted && this.$refs?.calendar ? this.$refs.calendar.times.now.hour * 60 + this.$refs.calendar.times.now.minute + timezoneDiffInMinutes : null;
	}

	/**
	 * Determine if current time indicator should be shown based on selected range and calendar type
	 * Only show for current day in day view or current week in week view
	 *
	 * @return {boolean}
	 */
	private get showCurrentTimeIndicator(): boolean {
		const now = DateTime.now();
		const startDate = DateTime.fromISO(this.selectedRange.start);
		const endDate = DateTime.fromISO(this.selectedRange.end);

		// Show indicator only if current date is within selected range
		return !!this.currentTime && ((now >= startDate && now <= endDate) || now.startOf('day').toISODate() === startDate.startOf('day').toISODate()) && (this.calendarType === 'day' || this.calendarType === 'week');
	}

	/**
	 * Combine calendar and temporary calendar events (create new order) for display on weekly and daily views
	 * Orders cannot be created from the monthly view, so only return calendar events
	 *
	 * @return {(CalendarEvent | TempCalendarEvent)[] | MonthlyCalendarEvent[]}
	 */
	private get mutableCalendarEvents(): (CalendarEvent | TempCalendarEvent)[] | MonthlyCalendarEvent[] {
		if ((this.calendarType === 'week' || this.calendarType === 'day') && this.tempCalendarEvent) {
			return [...(this.calendarEvents as CalendarEvent[]), this.tempCalendarEvent];
		}
		return this.calendarEvents;
	}

	/**
	 * Setup interval to update current time indicator every minute
	 *
	 * @return {void}
	 */
	private mounted(): void {
		this.$nextTick(() => {
			this.calendarMounted = true;
			this.timeUpdateInterval = window.setInterval(() => this.$refs.calendar.updateTimes(), 60 * 1000);
		});
		this.switchToDayView();

		window.addEventListener('resize', this.handleResize);
	}

	/**
	 * Cleanup time update interval when component is destroyed
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		if (this.timeUpdateInterval) {
			clearInterval(this.timeUpdateInterval);
		}
		window.removeEventListener('resize', this.handleResize);
	}

	/**
	 * Handle window resize - switch to day view if needed and recalculate menu position
	 *
	 * @return {void}
	 */
	private handleResize(): void {
		this.switchToDayView();
		this.calculateMenuPosition();
	}

	/**
	 * Switch to day view if container width is less than or equal to 625px
	 * Calendar type select will be hidden by css in this case
	 *
	 * @return {void}
	 */
	private switchToDayView(): void {
		this.parentContainerWidth = document.getElementById('dashboard')?.offsetWidth;

		if (this.parentContainerWidth && this.parentContainerWidth <= 625 && this.calendarType !== 'day') {
			this.$emit('calendar-type-change', { type: 'day', date: DateTime.fromISO(this.selectedRange.start).toISODate() });
			this.isCreateOrderMenuOpen = false;
		}
	}

	/**
	 * Check if a given date is marked as closed in restaurant holiday hours
	 *
	 * @param {string} date
	 * @return {boolean}
	 */
	private isClosedDate(date: string): boolean {
		if (!this.restaurant.holiday_hours?.length) {
			return false;
		}
		const orderDate = DateTime.fromISO(date).toISODate();
		return this.restaurant.holiday_hours.some(holiday => holiday.date === orderDate);
	}

	/**
	 * Check if given interval is in the future
	 * If false the interval cannot be clicked on to create a new order
	 *
	 * @param {string} date
	 * @param {string} time
	 * @param {number} quarter
	 * @return {boolean}
	 */
	private isIntervalInTheFuture(date: string, time: string, quarter: number): boolean {
		const intervalDateTime = DateTime.fromISO(`${date}T${time}`, { zone: this.timezone }).set({ minute: quarter });
		return intervalDateTime > DateTime.now().setZone(this.timezone);
	}

	/**
	 * Creates a temporary order on the calendar and opens the popover for creating a new order
	 *
	 * @param {MouseEvent} event
	 * @param {string} date
	 * @param {string} time
	 * @param {number} quarter
	 * @return {void}
	 */
	private openCreateOrderPopover(event: MouseEvent, date: string, time: string, quarter: number): void {
		if (this.tempCalendarEvent) {
			this.isCreateOrderMenuOpen = false;
			this.tempCalendarEvent = null;
			this.clickedElement = null;
		}
		else {
			// Store the clicked element for resize handling
			this.clickedElement = event.target as HTMLElement;

			// Get the position of the clicked element on the page
			this.calculateMenuPosition();

			this.isCreateOrderMenuOpen = true;
			const tempEventTime = DateTime.fromISO(`${date}T${time}`).set({ minute: quarter });
			this.tempOrderTime = tempEventTime.setLocale(this.locale).toLocaleString(DateTime.DATETIME_MED);
			this.tempCalendarEvent = {
				start: tempEventTime.toFormat('yyyy-MM-dd HH:mm'),
				end: tempEventTime.plus({ minutes: this.calendarType === 'day' ? 30 : 60 }).toFormat('yyyy-MM-dd HH:mm'),
				time: tempEventTime.toFormat(this.$t('orders.management.parent_card.dates.time_format')),
				name: this.$t('orders.management.new_order_btn')
			};
		}
	}

	/**
	 * Calculate the position for the create order popover based depending on calendar type
	 *
	 * @return {void}
	 */
	private calculateMenuPosition(): void {
		if (this.clickedElement) {
			const rect = this.clickedElement.getBoundingClientRect();

			const menuWidth = 250;
			const menuHeight = 145;
			const menuOffset = 5;

			// Calculate menu horizontal positioning for week view
			if (this.calendarType === 'week') {
				const pageWidth = window.innerWidth;
				// Check if there's enough room on the right side
				if (rect.right + menuOffset + menuWidth > pageWidth) {
					this.menuPositionX = rect.left - menuWidth - menuOffset;
				}
				else {
					// Position menu to the right of the clicked element
					this.menuPositionX = rect.right + menuOffset;
				}

				this.menuPositionY = rect.top;
			}
			// Calculation menu vertical positioning for day view
			else if (this.calendarType === 'day') {
				// Check if there's enough vertical height for the menu to be positioned
				if (rect.top - menuOffset - menuHeight < 0) {
					this.menuPositionY = rect.bottom + menuOffset;
				}
				else {
					this.menuPositionY = rect.top - menuOffset - menuHeight;
				}

				this.menuPositionX = rect.left;
			}
		}
	}

	/**
	 * Set order date and time, then navigate to the create order page
	 *
	 * @return {void}
	 */
	private navigateToOrderPage(): void {
		if (this.tempCalendarEvent) {
			this.setTakeoutInformation({
				...this.pickupInformation,
				dueByDate: DateTime.fromFormat(this.tempCalendarEvent.start, 'yyyy-MM-dd HH:mm').toISO(),
				dueByTime: this.tempCalendarEvent.time
			});
			this.$router.push('/order-management/create').catch(() => {});
		}
	}

	/**
	 * Format time interval display for calendar hour labels
	 *
	 * @param {Record<string, any>} date
	 * @return {string}
	 */
	private formatInterval(date: Record<string, any>): string {
		const format = this.parentContainerWidth && this.parentContainerWidth <= 625
			? this.$t('orders.dashboard.calendar.interval_time_short')
			: this.$t('orders.dashboard.calendar.interval_time');
		return DateTime.fromFormat(date.time, 'hh:mm').toFormat(format);
	}

	/**
	 * Navigate calendar to previous period (day/week/month)
	 * Used by parent component when navigating date ranges
	 *
	 * @param {number} count
	 * @return {void}
	 */
	public prev(count: number): void {
		this.$refs.calendar.prev(count);
	}

	/**
	 * Navigate calendar to next period (day/week/month)
	 * Used by parent component when navigating date ranges
	 *
	 * @param {number} count
	 * @return {void}
	 */
	public next(count: number): void {
		this.$refs.calendar.next(count);
	}

	/**
	 * Scroll calendar to current time position
	 * Used by parent component when switching to day/week views
	 *
	 * @return {void}
	 */
	public scrollToTime(): void {
		// Custom scroll implementation for week view
		this.$nextTick(() => {
			const calendarBody = document.querySelector('.v-calendar-daily__scroll-area') as HTMLElement;
			if (calendarBody && this.currentTime && this.currentTime > 0) {
				// Scroll to current time position with some offset to center it better
				const scrollOffset = Math.max(0, this.currentTime - 200);
				calendarBody.scrollTo({ top: scrollOffset });
			}
		});
	}
}
